import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Search, X, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const ITEMS_PER_PAGE = 50;

const ItemSelectionList = React.memo(function ItemSelectionList({ paginatedItems, selectedSet, onToggle }) {
  if (paginatedItems.length === 0) {
    return <div className="p-4 text-center text-muted-foreground">No items found</div>;
  }
  return paginatedItems.map((item) => {
    const isSelected = selectedSet.has(item.sku);
    return (
      <div key={item.zoho_item_id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 cursor-pointer" onClick={() => onToggle(item.sku)}>
        <div className={`h-4 w-4 rounded-sm border shadow flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-primary'}`}>
          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
        </div>
        <span className="font-mono text-sm font-medium w-36 truncate">{item.sku}</span>
        <span className="text-muted-foreground text-sm truncate flex-1">{item.name}</span>
      </div>
    );
  });
});

export default function AddTargetModal({ open, onClose, items, onSubmit, submitting }) {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [itemTargets, setItemTargets] = useState({});
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [itemPage, setItemPage] = useState(0);

  useEffect(() => {
    if (open) {
      setSelectedItems(new Set());
      setItemTargets({});
      setEffectiveDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setItemSearch('');
      setItemPage(0);
    }
  }, [open]);

  const filteredItems = useMemo(() => {
    const validItems = items.filter(item => item.sku);
    if (!itemSearch.trim()) return validItems;
    const search = itemSearch.toLowerCase();
    return validItems.filter(item => item.sku.toLowerCase().includes(search) || (item.name && item.name.toLowerCase().includes(search)));
  }, [items, itemSearch]);

  const paginatedItems = useMemo(() => {
    const start = itemPage * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, itemPage]);

  const totalItemPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  useEffect(() => { setItemPage(0); }, [itemSearch]);

  const toggleItemSelection = useCallback((sku) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
        setItemTargets(t => { const n = { ...t }; delete n[sku]; return n; });
      } else {
        newSet.add(sku);
        setItemTargets(t => ({ ...t, [sku]: { target_price: '', expected_qty: '' } }));
      }
      return newSet;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      paginatedItems.forEach(item => {
        if (!newSet.has(item.sku)) {
          newSet.add(item.sku);
          setItemTargets(t => ({ ...t, [item.sku]: { target_price: '', expected_qty: '' } }));
        }
      });
      return newSet;
    });
  }, [paginatedItems]);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
    setItemTargets({});
  }, []);

  const updateItemTarget = (sku, field, value) => {
    setItemTargets(prev => ({ ...prev, [sku]: { ...prev[sku], [field]: value } }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedArray = Array.from(selectedItems);
    if (selectedArray.length === 0) { toast.error('Select at least one item'); return; }
    const missingTargets = selectedArray.filter(sku => { const t = itemTargets[sku]; return !t || !t.target_price; });
    if (missingTargets.length > 0) { toast.error(`Enter target price for all items. Missing: ${missingTargets.slice(0, 3).join(', ')}`); return; }
    onSubmit({ selectedItems: selectedArray, itemTargets, effectiveDate, notes });
  };

  const selectedCount = selectedItems.size;
  const selectedArray = Array.from(selectedItems);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Targets - Set Individual Prices</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="grid grid-cols-2 gap-4 border-b pb-4">
            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for targets..." className="rounded-sm" />
            </div>
          </div>

          <div className="flex-1 overflow-hidden flex gap-4">
            <div className="flex-1 overflow-hidden flex flex-col border-r pr-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Select Items ({selectedCount} selected)</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={selectAllOnPage}>Select Page</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>Clear All</Button>
                </div>
              </div>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search items..." value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} className="pl-10 rounded-sm" />
              </div>
              <div className="border rounded-sm flex-1 overflow-auto min-h-[200px]">
                <div className="divide-y">
                  <ItemSelectionList paginatedItems={paginatedItems} selectedSet={selectedItems} onToggle={toggleItemSelection} />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-muted-foreground">{filteredItems.length > 0 ? itemPage * ITEMS_PER_PAGE + 1 : 0}-{Math.min((itemPage + 1) * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length}</span>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => setItemPage(p => Math.max(0, p - 1))} disabled={itemPage === 0}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="px-2">Page {itemPage + 1}/{totalItemPages || 1}</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItemPage(p => Math.min(totalItemPages - 1, p + 1))} disabled={itemPage >= totalItemPages - 1}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
              <Label className="mb-2">Set Target Prices</Label>
              {selectedCount === 0 ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">Select items to set prices</div>
              ) : (
                <div className="border rounded-sm flex-1 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">SKU</TableHead>
                        <TableHead className="text-xs w-28">Target $</TableHead>
                        <TableHead className="text-xs w-20">Qty</TableHead>
                        <TableHead className="w-8"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedArray.map(sku => {
                        const item = items.find(i => i.sku === sku);
                        const t = itemTargets[sku] || {};
                        return (
                          <TableRow key={sku}>
                            <TableCell className="py-1">
                              <div className="font-mono text-xs truncate max-w-[140px]" title={sku}>{sku}</div>
                              {item && <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">{item.name}</div>}
                            </TableCell>
                            <TableCell className="py-1">
                              <Input type="number" step="0.01" value={t.target_price || ''} onChange={(e) => updateItemTarget(sku, 'target_price', e.target.value)} className="h-7 text-xs font-mono rounded-sm" placeholder="0.00" />
                            </TableCell>
                            <TableCell className="py-1">
                              <Input type="number" value={t.expected_qty || ''} onChange={(e) => updateItemTarget(sku, 'expected_qty', e.target.value)} className="h-7 text-xs font-mono rounded-sm" placeholder="0" />
                            </TableCell>
                            <TableCell className="py-1">
                              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleItemSelection(sku)}><X className="h-3 w-3" /></Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={submitting || selectedCount === 0} className="rounded-sm" data-testid="save-target-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create ${selectedCount} Target(s)`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
